import { Component } from '@theme/component';

/**
 * @typedef {object} ProductTabsRefs
 * @property {HTMLButtonElement[]} tabs - The tab buttons.
 * @property {HTMLElement[]} panels - The tab panels.
 */

/**
 * A custom element that switches between the panels of the product tabs block.
 *
 * @extends Component<ProductTabsRefs>
 */
class ProductTabs extends Component {
  /**
   * Activates the tab at the given index.
   *
   * @param {number | string} index - The index of the tab to activate.
   */
  select(index) {
    this.#activate(Number(index));
  }

  /**
   * Moves between tabs with the keyboard, as expected of a tablist.
   *
   * @param {number | string} index - The index of the focused tab.
   * @param {KeyboardEvent} event - The keydown event.
   */
  navigate(index, event) {
    const tabs = this.refs.tabs ?? [];
    const current = Number(index);
    const last = tabs.length - 1;

    /** @type {number | null} */
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
        next = current === last ? 0 : current + 1;
        break;
      case 'ArrowLeft':
        next = current === 0 ? last : current - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(next, true);
  }

  /**
   * @param {number} index - The index of the tab to activate.
   * @param {boolean} [focusTab] - Whether to move focus to the activated tab.
   */
  #activate(index, focusTab = false) {
    const { tabs = [], panels = [] } = this.refs;

    tabs.forEach((tab, tabIndex) => {
      const isActive = tabIndex === index;
      const panel = panels[tabIndex];

      tab.classList.toggle('product-tabs__tab--active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');

      if (panel) panel.hidden = !isActive;
    });

    if (focusTab) tabs[index]?.focus();
  }
}

if (!customElements.get('product-tabs-component')) {
  customElements.define('product-tabs-component', ProductTabs);
}
